import { useMutation, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { api } from 'src/common/utils/api';

type SubstantiateResponse = {
  markdown: string;
};

const SUBSTANTIATE_MUTATION_KEY = ['substantiate-meeting-note'];

const useSubstantiatingStore = create<{
  noteIds: Set<string>;
  add: (id: string) => void;
  remove: (id: string) => void;
}>(set => ({
  noteIds: new Set(),
  add: (id: string) =>
    set(state => {
      const next = new Set(state.noteIds);
      next.add(id);
      return { noteIds: next };
    }),
  remove: (id: string) =>
    set(state => {
      const next = new Set(state.noteIds);
      next.delete(id);
      return { noteIds: next };
    }),
}));

export const useSubstantiateMeetingNote = (clientId: string, invalidateKeys?: string[][]) => {
  const queryClient = useQueryClient();
  const { add, remove } = useSubstantiatingStore();

  const { mutateAsync, isPending } = useMutation<{ data: SubstantiateResponse }, Error, string>({
    mutationKey: SUBSTANTIATE_MUTATION_KEY,
    mutationFn: (meetingNoteId: string) =>
      api.post<SubstantiateResponse>(
        `clients/${clientId}/meeting-notes/${meetingNoteId}/substantiate`,
        undefined,
        { timeoutMs: 120_000 },
      ),
    onSuccess: (_data, meetingNoteId) => {
      remove(meetingNoteId);
      const keys = invalidateKeys ?? [['clients', clientId, 'meeting-notes']];
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (_error, meetingNoteId) => {
      remove(meetingNoteId);
    },
  });

  const substantiate = (meetingNoteId: string) => {
    add(meetingNoteId);
    return mutateAsync(meetingNoteId);
  };

  return {
    substantiate,
    isSubstantiating: isPending,
  };
};

export const useIsNoteSubstantiating = (noteId: string): boolean => {
  return useSubstantiatingStore(state => state.noteIds.has(noteId));
};
